import axios, { AxiosError } from 'axios'
import qs from 'qs'

import { runAppInit } from '@app/appInit'
import config from '@app/config'
import { removeBuyerViewQueryParam } from '@app/utils/url/removeBuyerViewQueryParam'
import { sendEventAuth } from '@app/utils/webview/events'
import { hasFeature, WebViewFeature } from '@app/utils/webview/hasFeature'

import { logout } from '..'
import {
  ABORT_REASON_403_REINIT,
  API_INSTANCE_DEFAULT_HEADERS,
  pendingControllers,
} from './constants'
import { AuthorizationMiddleware, UserAgentMiddleware } from './middlewares'
import { getRefreshedToken } from './tokenRefreshManager'
import { AxiosRequestConfigWithMeta } from './types'

let isReinitializing = false
let reinitPromise: Promise<void> | null = null

export const createInstance = ({ requireAuth = true, ...params }) => {
  const instance = axios.create({
    headers: API_INSTANCE_DEFAULT_HEADERS,
    paramsSerializer: (params) => qs.stringify(params),
    ...params,
  })

  instance.interceptors.request.use((config) => {
    const controller = new AbortController()
    const originalSignal = config.signal

    if (originalSignal) {
      if (originalSignal.aborted) {
        controller.abort((originalSignal as AbortSignal).reason)
      } else {
        originalSignal.addEventListener?.('abort', () => {
          controller.abort((originalSignal as AbortSignal).reason)
        })
      }
    }

    const configWithAbort = config as AxiosRequestConfigWithMeta
    configWithAbort._abortController = controller

    config.signal = controller.signal
    pendingControllers.add(controller)
    return config
  })

  instance.interceptors.request.use(UserAgentMiddleware, (error) => {
    console.log('Error adding user agent:', error)
    return Promise.reject(error)
  })

  if (requireAuth) {
    instance.interceptors.request.use(AuthorizationMiddleware, (error) => {
      console.log('Error in AuthorizationMiddleware:', error)
      return Promise.reject(error)
    })

    instance.interceptors.response.use(
      (response) => {
        const ctrl = (response.config as AxiosRequestConfigWithMeta)
          ._abortController
        if (ctrl) pendingControllers.delete(ctrl)
        return response
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as
          | AxiosRequestConfigWithMeta
          | undefined
        const status = error.response?.status

        if (status === 403 && !originalRequest?._skipReinitOn403) {
          if (isReinitializing && reinitPromise) {
            removeBuyerViewQueryParam()
            await reinitPromise
            if (originalRequest && !originalRequest._reinitRetry403) {
              originalRequest._reinitRetry403 = true
              return instance(originalRequest)
            }

            return Promise.reject(error)
          }

          if (!originalRequest || originalRequest._reinitRetry403) {
            return Promise.reject(error)
          }

          isReinitializing = true
          pendingControllers.forEach((controller) =>
            controller.abort(ABORT_REASON_403_REINIT)
          )
          pendingControllers.clear()

          if (!reinitPromise) {
            reinitPromise = runAppInit()
          }

          try {
            await reinitPromise
          } finally {
            reinitPromise = null
            isReinitializing = false
          }

          originalRequest._reinitRetry403 = true
          return instance(originalRequest)
        }

        const ctrl = originalRequest?._abortController
        if (ctrl) pendingControllers.delete(ctrl)

        if (status === 401 && originalRequest?.headers) {
          try {
            const refreshed = await getRefreshedToken(true)
            if (refreshed && refreshed.token) {
              originalRequest.headers.Authorization = `Bearer ${refreshed.token}`
              return instance(originalRequest)
            }

            const canDelegateRefresh =
              config.identity.isWebView &&
              hasFeature(WebViewFeature.DelegatedJwtExchange)

            if (
              (!refreshed || !refreshed.refreshToken) &&
              !canDelegateRefresh
            ) {
              logout(originalRequest)
              return
            }

            if (
              config.identity.isWebView &&
              !hasFeature(WebViewFeature.DelegatedJwtExchange)
            ) {
              sendEventAuth(refreshed?.token, refreshed?.refreshToken)
            }
          } catch (refreshError) {
            return Promise.reject(refreshError)
          }
        }

        return Promise.reject(error)
      }
    )
  }

  return instance
}
